// PRESTOplayPlayerAdapter.js
'use strict';

import {
  getWidevineRobustnessCustomData,
  loadPrestoplayScript,
  setWidevineServerCertificate,
} from '../resource/loadPrestoplayScript.js';

import BasePlayerAdapter from '../resource/BasePlayerAdapter.js';
import { PlayerAdapterEvent } from '../resource/playerAdapterConstants.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import {
  COM_APPLE_FPS,
  COM_WIDEVINE_ALPHA,
  PRESTOPLAY_DRM_ENV_NAME,
} from '../resource/drmConstants.js';
import { getHeader } from '../resource/fetchOptionHeader.js';
import { getWidevineServerCertificateBuffer } from '../resource/formatVodDrmSource.js';
import { DEFAULT_HLS_CODECS, formatSource } from '../resource/playerUtils.js';

class PRESTOplayPlayerAdapter extends BasePlayerAdapter {
  static async ensureReady() {
    await loadPrestoplayScript();
  }

  constructor(options = {}) {
    super(options);

    this._prestoPlayer = null;
    this._eventListeners = new Map();
    this._playerOptions = options.playerOptions || {};

    this._getToken = options.getToken || NULL_FUNCTION;
    this._getClientId = options.getClientId || NULL_FUNCTION;

    this._container = null;
    this._prestoEventHandlers = [];
    this._boundVolumeChange = null;
  }

  _setupAllEvents() {
    const player = this._prestoPlayer;

    const { events, Player } = this._clpp;

    // STATE_CHANGED → PLAYING, PAUSED, ENDED
    this._onPrestoEvent(player, events.STATE_CHANGED, event => {
      const currentState = event.detail?.currentState;
      switch (currentState) {
        case Player.State.PLAYING:
          this._emit(PlayerAdapterEvent.PLAYING, event.detail);
          break;
        case Player.State.PAUSED:
          this._emit(PlayerAdapterEvent.PAUSED, event.detail);
          break;
        case Player.State.ENDED:
          this._emit(PlayerAdapterEvent.ENDED, event.detail);
          break;
        default:
          break;
      }
    });

    this._onPrestoEvent(player, events.ERROR, event => {
      this._emit(PlayerAdapterEvent.ERROR, event.detail || event);
    });

    this._onPrestoEvent(player, events.LOADEDMETADATA, () => {
      this._emit(PlayerAdapterEvent.READY);
    });

    this._onPrestoEvent(player, events.SEEKING, () => {
      this._emit(PlayerAdapterEvent.SEEK);
    });

    this._onPrestoEvent(player, events.SEEKED, () => {
      this._emit(PlayerAdapterEvent.SEEKED);
    });

    this._onPrestoEvent(player, events.BUFFERING_STARTED, event => {
      this._emit(PlayerAdapterEvent.STALL_STARTED, event.detail || {});
    });

    this._onPrestoEvent(player, events.BUFFERING_ENDED, event => {
      this._emit(PlayerAdapterEvent.STALL_ENDED, event.detail || {});
    });

    // PRESTO proxies HTML5 events — listen for timeupdate directly.
    this._onPrestoEvent(player, 'timeupdate', () => {
      this._emit(PlayerAdapterEvent.TIME_CHANGED, player.getPosition());
    });

    // PRESTO has no native MUTED/UNMUTED events — listen to video element.
    this._boundVolumeChange = () => {
      this._emit(PlayerAdapterEvent.VOLUME_CHANGED, {
        volume: this._videoElement?.volume ?? 1,
      });
    };
    this._videoElement?.addEventListener(
      'volumechange',
      this._boundVolumeChange
    );
  }

  _onPrestoEvent(player, eventName, handler) {
    player.on(eventName, handler);
    this._prestoEventHandlers.push({ eventName, handler });
  }

  _removeAllPrestoEvents() {
    if (this._prestoPlayer) {
      for (const { eventName, handler } of this._prestoEventHandlers) {
        try {
          this._prestoPlayer.off(eventName, handler);
        } catch {
          // Player may already be destroyed.
        }
      }
    }
    this._prestoEventHandlers = [];

    if (this._boundVolumeChange && this._videoElement) {
      this._videoElement.removeEventListener(
        'volumechange',
        this._boundVolumeChange
      );
    }
    this._boundVolumeChange = null;
  }

  _emit(event, data) {
    const listeners = this._eventListeners.get(event);
    if (listeners) {
      for (const callback of listeners) {
        try {
          callback(data);
        } catch (error) {
          this._logError('_emit', error);
        }
      }
    }
  }

  async initialize(videoElement) {
    this._guardDestroyed();

    if (this._prestoPlayer) {
      this._log('initialize', 'already initialized, skipping');
      return;
    }

    this._clpp = await loadPrestoplayScript();

    this._videoElement = videoElement;

    // Wrap video element in a container to prevent PRESTO auto-wrapping DOM moves.
    this._container = document.createElement('div');
    if (videoElement.parentNode) {
      videoElement.parentNode.insertBefore(this._container, videoElement);
    }
    this._container.appendChild(videoElement);

    const playerConfig = this._playerOptions.license
      ? {
          license: this._playerOptions.license,
          viewerId: getHeader('X-Client-ID') || 'anonymous',
        }
      : {};

    this._prestoPlayer = new this._clpp.Player(videoElement, playerConfig, {
      containerEl: this._container,
    });

    this._setupAllEvents();
  }

  async load(sourceConfig) {
    this._guardDestroyed();

    if (!this._prestoPlayer) {
      throw new Error('Player not created');
    }

    if (sourceConfig.drm) {
      await this._loadWithDrm(sourceConfig);
      return;
    }

    await this._loadWithoutDrm(sourceConfig);
  }

  async _loadWithDrm(sourceConfig) {
    const { src, drm } = sourceConfig;
    const { dash, hls } = src || {};
    const { keySystem, robustness, widevineServerCertificateBase64 } = drm;

    const isFairPlay = keySystem === COM_APPLE_FPS;
    const manifestUrl = isFairPlay ? hls : dash;

    const authToken = drm.drmToken || this._getToken() || '';
    const clientId = this._getClientId() || getHeader('X-Client-ID') || '';

    if (widevineServerCertificateBase64) {
      setWidevineServerCertificate({
        serverCertificate: getWidevineServerCertificateBuffer({
          widevineServerCertificateBase64,
        }),
      });
    }

    const widevineRobustnessCustomData =
      keySystem === COM_WIDEVINE_ALPHA && robustness
        ? getWidevineRobustnessCustomData({
            // Use lowest capability — due to DRM detection only tests video capability.
            audioRobustness: ['SW_SECURE_CRYPTO'],
            videoRobustness: [robustness],
          })
        : {};

    const source = isFairPlay
      ? { url: manifestUrl, type: this._clpp.Type.HLS, drmProtected: true }
      : { url: manifestUrl, type: this._clpp.Type.DASH, drmProtected: true };

    const openConfig = {
      source,
      drm: {
        env: PRESTOPLAY_DRM_ENV_NAME,
        preferredDrmSystem: keySystem,
        customData: {
          token: authToken,
          clientId,
          drmToken: drm.drmToken,
          ...widevineRobustnessCustomData,
        },
      },
    };

    await this._executeOpen(openConfig);
  }

  async _loadWithoutDrm(sourceConfig) {
    const { src } = sourceConfig;
    const [primarySource] = formatSource(src || {});

    const source =
      primarySource?.typeKey === 'dash'
        ? { url: primarySource.src, type: this._clpp.Type.DASH }
        : primarySource?.typeKey === 'hls'
          ? { url: primarySource.src, type: this._clpp.Type.HLS }
          : primarySource?.src || '';

    await this._executeOpen({ source, hlsDefaultCodecs: src?.hlsCodecs });
  }

  async _executeOpen(openConfig) {
    try {
      const { hlsDefaultCodecs, ...prestoOpenConfig } = openConfig;

      // iOS autoplay requires muted. Set before open() to satisfy autoplay policy.
      this._prestoPlayer.setMuted(true);
      await this._prestoPlayer.open({
        ...prestoOpenConfig,
        streaming: {
          // 0.5s rebuffer → show first frame ASAP; raise if rebuffer spikes
          rebufferingGoal: 0.5,
        },
        manifest: {
          // Standalone media playlists lack CODECS; use preserved master
          // CODECS when AES collapse has it, otherwise keep the legacy fallback.
          hlsDefaultCodecs: hlsDefaultCodecs || DEFAULT_HLS_CODECS,
        },
      });
      // PRESTOplay open() rebuilds the media pipeline and resets muted state; re-apply.
      this._prestoPlayer.setMuted(true);
    } catch (error) {
      this._logError('load', error);
      throw error;
    }
  }

  async unload() {
    this._guardDestroyed();

    if (this._prestoPlayer) {
      try {
        await this._prestoPlayer.release();
      } catch (error) {
        this._logError('unload', error);
      }
    }
  }

  async destroy() {
    if (this._isDestroyed) return;

    this._isDestroyed = true;
    this._removeAllPrestoEvents();

    if (this._prestoPlayer) {
      try {
        await this._prestoPlayer.destroy();
      } catch (error) {
        this._logError('destroy', error);
      }
      this._prestoPlayer = null;
    }

    // Restore video element to its original position before removing container.
    if (this._container && this._videoElement) {
      if (this._container.parentNode) {
        this._container.parentNode.insertBefore(
          this._videoElement,
          this._container
        );
      }
      this._container.remove();
    }

    this._videoElement = null;
    this._container = null;
    this._eventListeners.clear();
  }

  async play() {
    this._guardDestroyed();
    return this._prestoPlayer?.play();
  }

  pause() {
    if (this._isDestroyed) return;
    // ponytail: pause() returns a Promise that rejects if player state disallows it (e.g. after failed load)
    this._prestoPlayer?.pause()?.catch(() => {});
  }

  mute() {
    this._guardDestroyed();
    this._prestoPlayer?.setMuted(true);
    this._emit(PlayerAdapterEvent.MUTED);
  }

  unmute() {
    this._guardDestroyed();
    this._prestoPlayer?.setMuted(false);
    this._emit(PlayerAdapterEvent.UNMUTED);
  }

  isMuted() {
    this._guardDestroyed();
    return this._prestoPlayer?.isMuted() ?? true;
  }

  seek(time) {
    this._guardDestroyed();
    this._prestoPlayer?.setPosition(time);
  }

  getCurrentTime() {
    this._guardDestroyed();
    return this._prestoPlayer?.getPosition() ?? 0;
  }

  getDuration() {
    this._guardDestroyed();
    return this._prestoPlayer?.getDuration() ?? 0;
  }

  setPlaybackRate(rate) {
    this._guardDestroyed();
    this._prestoPlayer?.setRate(rate);
  }

  getPlaybackRate() {
    this._guardDestroyed();
    return this._prestoPlayer?.getRate() ?? 1;
  }

  on(event, callback) {
    this._guardDestroyed();

    if (!this._eventListeners.has(event)) {
      this._eventListeners.set(event, new Set());
    }
    this._eventListeners.get(event).add(callback);
  }

  off(event, callback) {
    if (this._isDestroyed) return;
    this._eventListeners.get(event)?.delete(callback);
  }

  getInternalPlayer() {
    return this._prestoPlayer;
  }

  getContainer() {
    return this._container || null;
  }
}

export default PRESTOplayPlayerAdapter;
